import { DEFAULT_COLUMN_SETTINGS } from 'components/shared/RosterViewDataGrid/const';

import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { ReactNode } from 'react';
import type {
  ProtocolRulePeriodResponse,
  ProtocolRuleResponse
} from 'generated/index';
import type { ColumnBuilder } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';
import type { PrescriptionRuleCellValue } from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRuleCell';
import type {
  ProtocolRuleCells,
  ProtocolRuleRow
} from './ProtocolRuleGrid.types';

/** A phase as the grid needs it; a phase still being edited has no id yet */
export type ProtocolRulePhase = Omit<ProtocolRulePeriodResponse, 'id'>;

/** Name of the grid's custom timeline view: relative weeks, which have no calendar dates */
export const RELATIVE_WEEKS_VIEW = 'relativeWeeks';

export const getWeekField = (week: number): string => `w${week}`;

/**
 * A cell spans its weeks but shows only the count: no `totalWeeks`, so unlike a prescription cell there
 * is no "(Nw)" after it — the span itself shows how many weeks it covers.
 */
const toCell = (
  frequency: number,
  span: number
): PrescriptionRuleCellValue => ({
  frequency,
  cellColSpan: span
});

/**
 * The cells of one phase, keyed by the week they start in. A TOTAL (fixed) phase is one cell across its
 * whole range; a WEEKLY (periodic) phase is one cell per interval, the last one cut off at the phase end.
 */
const buildPhaseCells = (
  {
    frequency,
    startPeriodNumber,
    endPeriodNumber,
    interval,
    recurrenceType
  }: ProtocolRulePhase,
  weekCount: number
): [number, PrescriptionRuleCellValue][] => {
  const start = Math.max(1, startPeriodNumber);
  const end = Math.min(endPeriodNumber, weekCount);
  if (start > end) {
    return [];
  }
  if (recurrenceType === 'TOTAL') {
    return [[start, toCell(frequency, end - start + 1)]];
  }

  const step = Math.max(1, interval);
  const cells: [number, PrescriptionRuleCellValue][] = [];
  for (let week = start; week <= end; week += step) {
    cells.push([week, toCell(frequency, Math.min(step, end - week + 1))]);
  }
  return cells;
};

export const buildProtocolRuleCells = (
  { periods }: { periods: ProtocolRulePhase[] },
  weekCount: number
): ProtocolRuleCells => {
  const cells: ProtocolRuleCells = {};
  for (let week = 1; week <= weekCount; week++) {
    cells[getWeekField(week)] = null;
  }
  periods.forEach((period) =>
    buildPhaseCells(period, weekCount).forEach(([week, cell]) => {
      cells[getWeekField(week)] = cell;
    })
  );
  return cells;
};

/** Sessions over the whole rule: the sum of its cells */
export const getRuleTotal = (cells: ProtocolRuleCells): number =>
  Object.values(cells).reduce(
    (total, cell) => total + (cell?.frequency ?? 0),
    0
  );

export const buildProtocolRuleRows = (
  rules: ProtocolRuleResponse[],
  weekCount: number
): ProtocolRuleRow[] =>
  rules.map((rule) => {
    const cells = buildProtocolRuleCells(rule, weekCount);
    return {
      ...cells,
      id: rule.id,
      activityName: rule.activity.name,
      description: rule.description,
      total: getRuleTotal(cells),
      rule
    };
  });

/**
 * Columns `w1` to `w{weekCount}` for the shared roster grid. A protocol runs on relative weeks, so
 * unlike the grid's own week views there are no calendar weeks or dates to derive them from.
 */
export const createRelativeWeekColumns =
  (weekCount: number, columnWidth: number): ColumnBuilder<ProtocolRuleRow> =>
  ({ renderTimelineCell }) => ({
    timeLineColumns: Array.from(
      { length: weekCount },
      (_, index): GridColDef<ProtocolRuleRow> => ({
        ...(DEFAULT_COLUMN_SETTINGS as Partial<GridColDef<ProtocolRuleRow>>),
        ...(renderTimelineCell
          ? {
              renderCell: renderTimelineCell as (
                params: GridRenderCellParams<ProtocolRuleRow>
              ) => ReactNode
            }
          : {}),
        field: getWeekField(index + 1),
        headerName: getWeekField(index + 1),
        colSpan: (value: PrescriptionRuleCellValue | null) =>
          value?.cellColSpan ?? 1,
        width: columnWidth
      })
    ),
    columnGroupingModel: []
  });
